/**
 * Account name form hook (APP-002 §8, §33).
 *
 * Manages the rename flow: Zod validation against backend constraints, an
 * explicit `{ name }` body (never arbitrary fields), DIRECT-011 field-error
 * mapping, duplicate-submission prevention, and query-cache synchronization
 * after success. The form is also used during onboarding's account-setup step.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { updateAccountName } from '@/api/account';
import { queryKeys } from '@/api/queryKeys';
import { isApiError, type ApiError, type FieldError } from '@/api/errors';

/**
 * Backend constraints for the account name. The OpenAPI `AccountPatchRequest`
 * types `name` as `string | null` (optional). We enforce a sensible frontend
 * floor: a trimmed, non-empty name of bounded length. The backend remains the
 * authority; server-returned field errors override these.
 */
const MIN = 1;
const MAX = 100;

export const accountNameSchema = z
  .string()
  .trim()
  .min(MIN, 'Enter an account name.')
  .max(MAX, `Account name must be ${MAX} characters or fewer.`);

export const ACCOUNT_NAME_MIN = MIN;
export const ACCOUNT_NAME_MAX = MAX;

export interface AccountNameFormState {
  value: string;
  setValue: (next: string) => void;
  /** Client-side validation error message, or null. */
  clientError: string | null;
  /** Server-side field error from DIRECT-011, or null. */
  serverFieldError: string | null;
  /** Page-level server error message (non-field), or null. */
  serverError: string | null;
  /** Request ID of the last failure, preserved for support. */
  requestId: string | undefined;
  isSubmitting: boolean;
  isSuccess: boolean;
  /** Submit the rename. Returns true on success. */
  submit: () => Promise<boolean>;
  /** Reset to the initial value and clear errors. */
  reset: () => void;
}

/**
 * Drive the account-name form. `initialName` is the current backend value
 * (prefilled). On success, the account identity + overview queries are
 * invalidated so the new name propagates (APP-002 §29).
 */
export function useAccountNameForm(initialName: string): AccountNameFormState {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(initialName);
  const [clientError, setClientError] = useState<string | null>(null);
  const [serverFieldError, setServerFieldError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [isSuccess, setIsSuccess] = useState(false);

  // Keep the field prefilled if the backend value changes externally.
  useEffect(() => {
    setValue(initialName);
  }, [initialName]);

  const mutation = useMutation({
    mutationFn: (next: string) => updateAccountName(next),
    onSuccess: () => {
      // Invalidate the bootstrap identity + overview so the new name and any
      // dependent overview fields propagate. Scope is narrow (APP-002 §29).
      void queryClient.invalidateQueries({ queryKey: queryKeys.account.identity() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.account.detail() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.overview.detail() });
      setIsSuccess(true);
    },
    onError: (err: unknown) => {
      if (!isApiError(err)) {
        setServerError('Could not update the account name. Please try again.');
        return;
      }
      const apiErr = err as ApiError;
      setRequestId(apiErr.requestId);
      const fieldErr = (apiErr.fieldErrors ?? []).find((f: FieldError) => f.field === 'name');
      if (fieldErr) {
        setServerFieldError(fieldErr.message);
      } else {
        setServerError(apiErr.message || 'Could not update the account name. Please try again.');
      }
    },
  });

  async function submit(): Promise<boolean> {
    setClientError(null);
    setServerFieldError(null);
    setServerError(null);
    setRequestId(undefined);
    setIsSuccess(false);

    const parsed = accountNameSchema.safeParse(value);
    if (!parsed.success) {
      setClientError(parsed.error.issues[0]?.message ?? 'Enter a valid account name.');
      return false;
    }
    // No-op if unchanged (still allowed, but avoid a needless request).
    if (parsed.data.trim() === initialName.trim()) {
      setIsSuccess(true);
      return true;
    }

    try {
      await mutation.mutateAsync(parsed.data);
      return !mutation.isError;
    } catch {
      return false;
    }
  }

  function reset() {
    setValue(initialName);
    setClientError(null);
    setServerFieldError(null);
    setServerError(null);
    setRequestId(undefined);
    setIsSuccess(false);
    mutation.reset();
  }

  return {
    value,
    setValue,
    clientError,
    serverFieldError,
    serverError,
    requestId,
    isSubmitting: mutation.isPending,
    isSuccess: isSuccess && !mutation.isError,
    submit,
    reset,
  };
}
